'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  Heart,
  Gavel,
  MessageSquare,
  Calendar,
  User,
  LogOut,
  X,
  ArrowLeft,
  Wand2,
  Handshake,
  ShoppingBag,
} from 'lucide-react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import { useBranding } from '@/contexts/TenantContext';
import { CurrencySelector } from '@/components/ui/CurrencySelector';
import { createClient } from '@/lib/supabase/client';
import { cn } from '@/lib/utils';

export type PortalView = 'dashboard' | 'saved' | 'offers' | 'enquiries' | 'bookings' | 'orders' | 'profile' | 'spaces';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Nav items with paths relative to /account (locale will be prepended)
const navItems: { id: PortalView; label: string; icon: React.ElementType; path: string }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/account' },
  { id: 'saved', label: 'Saved Properties', icon: Heart, path: '/account/wishlists' },
  { id: 'spaces', label: 'Room Designer', icon: Wand2, path: '/account/spaces' },
  { id: 'orders', label: 'Orders', icon: ShoppingBag, path: '/account/orders' },
  { id: 'offers', label: 'My Offers', icon: Gavel, path: '/account/offers' },
  { id: 'enquiries', label: 'Enquiries', icon: MessageSquare, path: '/account/enquiries' },
  { id: 'bookings', label: 'Bookings', icon: Calendar, path: '/account/bookings' },
  { id: 'profile', label: 'Profile', icon: User, path: '/account/profile' },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const pathname = usePathname();
  const { signOut } = useCustomerAuth();
  const branding = useBranding();
  const [isPartner, setIsPartner] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      const role = data.user?.app_metadata?.route_royal_role;
      setIsPartner(role === 'PARTNER' || role === 'ADMIN');
    });
  }, []);

  // Extract locale from pathname (e.g., /en/account -> en)
  const getLocale = (): string => {
    const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
    return match?.[1] ?? 'en';
  };

  const locale = getLocale();

  // Build href with locale prefix
  const getHref = (path: string): string => {
    return `/${locale}${path}`;
  };

  // Determine current view from pathname
  const getCurrentView = (): PortalView => {
    if (pathname.includes('/wishlists')) return 'saved';
    if (pathname.includes('/spaces')) return 'spaces';
    if (pathname.includes('/orders')) return 'orders';
    if (pathname.includes('/offers')) return 'offers';
    if (pathname.includes('/enquiries')) return 'enquiries';
    if (pathname.includes('/bookings')) return 'bookings';
    if (pathname.includes('/profile')) return 'profile';
    return 'dashboard';
  };

  const currentView = getCurrentView();

  const sidebarClasses = cn(
    'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-stone-200 transform transition-transform duration-300 ease-in-out',
    isOpen ? 'translate-x-0' : '-translate-x-full',
    'lg:translate-x-0 lg:static lg:inset-auto'
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-stone-900/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Content */}
      <aside className={sidebarClasses}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-stone-100">
            <Link href={getHref('/')} className="flex items-center">
              {branding.logo ? (
                <Image
                  src={branding.logo}
                  alt={branding.brandName || 'Logo'}
                  width={120}
                  height={40}
                  className="h-8 w-auto object-contain"
                  priority
                />
              ) : (
                <h1 className="text-xl font-bold text-stone-900 tracking-tight">
                  {branding.brandName || 'Portal'}
                </h1>
              )}
            </Link>
            <button onClick={onClose} className="lg:hidden text-stone-500 hover:text-stone-900">
              <X size={20} />
            </button>
          </div>

          {/* Back to Website */}
          <div className="px-4 pt-4">
            <Link
              href={getHref('/')}
              onClick={onClose}
              className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-700 bg-stone-50 border border-stone-200 hover:bg-stone-100 hover:border-stone-300 transition-colors"
            >
              <ArrowLeft size={16} className="text-stone-500" />
              Back to Website
            </Link>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const isActive = currentView === item.id;
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  href={getHref(item.path)}
                  onClick={onClose}
                  className={cn(
                    'w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-stone-100 text-stone-900'
                      : 'text-stone-500 hover:bg-stone-50 hover:text-stone-900'
                  )}
                >
                  <span className={cn('mr-3', isActive ? 'text-stone-900' : 'text-stone-400')}>
                    <Icon size={20} />
                  </span>
                  {item.label}
                </Link>
              );
            })}

            {isPartner && (
              <>
                <div className="border-t border-stone-100 my-3" />
                <Link
                  href={getHref('/partner')}
                  onClick={onClose}
                  className="w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium text-stone-500 hover:bg-stone-50 hover:text-stone-900 transition-colors"
                >
                  <span className="mr-3 text-stone-400">
                    <Handshake size={20} />
                  </span>
                  Partner Portal
                </Link>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-stone-100">
            <div className="flex items-center justify-between px-4 py-2 mb-1">
              <span className="text-xs font-medium text-stone-400 uppercase tracking-wider">Currency</span>
              <CurrencySelector variant="compact" dropUp className="text-stone-600" />
            </div>
            <button
              onClick={signOut}
              className="w-full flex items-center px-4 py-3 text-sm font-medium text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <LogOut size={20} className="mr-3" />
              Sign Out
            </button>
            <div className="mt-4 px-4 text-xs text-stone-400">
              © {new Date().getFullYear()} {branding.brandName || 'Portal'}.
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
